/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import React, { useState } from 'react'
import { jsx, css } from '@emotion/react'
import { useParams } from 'react-router-dom'
import { useTranslation } from '@multilocale/react/index.js'
import IconLinkedin from '@stiloso/icons/IconLinkedin.js'
import colMd10 from '@waiterio/styles/bootstrap/colMd10.js'
import colLg8 from '@waiterio/styles/bootstrap/colLg8.js'
import container from '@waiterio/styles/bootstrap/container.js'
import row from '@waiterio/styles/bootstrap/row.js'
import languages from '../languages.js'
import masthead from '../styles/masthead.js'
import Layout from '../components/LayoutComponent.js'
import authors from '../authors.json'

const siteHeading = css`
  color: white;
  padding: 200px 0 150px;
  text-align: center;
  @media (max-width: 768px) {
    padding: 150px 0 86px;
  }
`

const siteTitle = css({
  display: 'block',
  lineHeight: '46px',
  fontSize: 36,
  margin: 0,
  '@media (max-width: 767px)': {
    fontSize: 32,
  },
})

const siteDescription = css({
  display: 'block',
  lineHeight: '26px',
  fontSize: 24,
  fontWeight: 300,
  marginTop: 10,
  marginBottom: 0,
  '@media (max-width: 767px)': {
    fontSize: 20,
  },
})

export const paths = languages.map(locale => ({
  path: `/blog/${locale}/authors/:authorId/`,
  props: {
    locale,
  },
}))

export const Head = ({ locale }) => {
  const { authorId } = useParams()
  const { t } = useTranslation(locale)
  const title = t('Successful Restaurant')
  const description = t('a blog on managing a thriving restaurant')
  let url = `/blog/${locale}/authors/${authorId}/`

  return (
    <>
      <title>{`${title} - Waiterio POS`}</title>
      <meta name="description" content={description} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={`https://www.waiterio.com${url}`} />
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={description} />
      {url && <link rel="canonical" href={`https://www.waiterio.com${url}`} />}
    </>
  )
}

const AuthorPage = ({ locale }) => {
  const { authorId } = useParams()
  const { t } = useTranslation(locale)
  let [showMoreText, setShowMoreText] = useState(false)

  const author = authors.find(author => author.id === authorId)
  let authorAvatarImageName
  if (authorId === 'melkins') {
    authorAvatarImageName = 'melkins-428d12f4'
  } else if (authorId === 'achatterjee') {
    authorAvatarImageName = 'achatterjee-d4bd5a26'
  }
  const authorAvatarImageUrl = `https://www.imagelato.com/images/author-image-${authorAvatarImageName}-320w`
  const { fullName, shortDescription, description, linkedInUrl } = author

  return (
    <Layout locale={locale}>
      <header css={masthead}>
        <div
          css={{
            backgroundColor: 'rgb(33, 37, 41)',
            height: '100%',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
          }}
        />
        <div css={container}>
          <div css={row}>
            <div
              css={[
                colMd10,
                colLg8,
                { marginLeft: 'auto', marginRight: 'auto' },
              ]}
            >
              <div css={siteHeading}>
                <picture>
                  <source
                    type="image/webp"
                    srcSet={`${authorAvatarImageUrl}.webp`}
                  />
                  <img
                    src={`${authorAvatarImageUrl}.jpg`}
                    css={{
                      marginBottom: 30,
                      textAlign: 'center',
                      width: 200,
                      height: 200,
                      borderRadius: 100,
                    }}
                  />
                </picture>
                <h1 css={siteTitle}>{fullName}</h1>
                <p css={siteDescription}>{shortDescription}</p>
                <article
                  css={[
                    siteDescription,
                    { display: showMoreText ? 'block' : 'none' },
                  ]}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
                <div
                  css={{
                    display: description ? 'inline-block' : 'none',
                    marginTop: 15,
                    cursor: 'pointer',
                    ':hover': { color: 'var(--color-primary)' },
                  }}
                  onClick={() => setShowMoreText(!showMoreText)}
                >
                  {!showMoreText ? 'Read more' : 'Read less'}
                </div>
                <div
                  css={{
                    marginTop: 30,
                    width: '100%',
                    height: 2,
                    backgroundColor: '#f5f5f5',
                  }}
                />
                <div
                  css={{
                    marginTop: 15,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <div css={{ margin: 'auto 0px' }}>
                    {t('Follow')} {fullName}
                  </div>
                  <a
                    href={linkedInUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <IconLinkedin
                      css={{
                        marginTop: 8,
                        marginLeft: 8,
                        fill: 'white',
                        width: 42,
                        height: 42,
                        cursor: 'pointer',
                      }}
                    />
                  </a>
                </div>
                <em>
                  For any enquiry about the content, please contact{' '}
                  <a
                    css={{ fontWeight: 'bold' }}
                    href="mailto:abhi@waiterio.com"
                  >
                    Abhi
                  </a>
                  .
                </em>
              </div>
            </div>
          </div>
        </div>
      </header>
    </Layout>
  )
}

export default AuthorPage
